import { FC, useCallback, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, Dialog, DialogActions, DialogContent, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material'

import GRDialogTitle from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { PromptReasoningLevel, PromptType } from '../../types/types'

type CreateDefinitionFormValues = {
  name: string
  type: PromptType
  fullText: string
  months: number
  reasoningLevel: PromptReasoningLevel
}

type CreateDefinitionDialogProps = {
  open: boolean
  onClose: () => void
  onSubmit: (values: CreateDefinitionFormValues) => void
  isLoading: boolean
}

const PROMPT_NAME_PATTERN = /^[A-Z0-9_]+$/

export const CreateDefinitionDialog: FC<CreateDefinitionDialogProps> = ({ open, onClose, onSubmit, isLoading }) => {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateDefinitionFormValues>({
    defaultValues: { name: '', type: 'text', fullText: '', months: 14, reasoningLevel: 'medium' },
  })

  const selectedType = watch('type')

  useEffect(() => {
    if (open) reset()
  }, [open, reset])

  const handleFormSubmit = useCallback(
    (values: CreateDefinitionFormValues) => {
      onSubmit(values)
    },
    [onSubmit]
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <GRDialogTitle onClose={onClose}>{t('internal-ai-prompts:create_definition_title')}</GRDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: true,
                pattern: { value: PROMPT_NAME_PATTERN, message: t('internal-ai-prompts:field_name_helper') },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('internal-ai-prompts:field_name')}
                  helperText={errors.name?.message || t('internal-ai-prompts:field_name_helper')}
                  error={!!errors.name}
                  fullWidth
                  size="small"
                />
              )}
            />
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel>{t('internal-ai-prompts:field_type')}</InputLabel>
                  <Select {...field} label={t('internal-ai-prompts:field_type')}>
                    <MenuItem value="text">{t('internal-ai-prompts:type_text')}</MenuItem>
                    <MenuItem value="image">{t('internal-ai-prompts:type_image')}</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="fullText"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('internal-ai-prompts:field_full_text')}
                  multiline
                  rows={10}
                  fullWidth
                  size="small"
                  error={!!errors.fullText}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />
              )}
            />
            {selectedType === 'text' && (
              <>
                <Controller
                  name="months"
                  control={control}
                  rules={{ required: true, min: 0 }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      type="number"
                      label={t('internal-ai-prompts:field_months')}
                      fullWidth
                      size="small"
                      error={!!errors.months}
                    />
                  )}
                />
                <Controller
                  name="reasoningLevel"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('internal-ai-prompts:field_reasoning_level')}</InputLabel>
                      <Select {...field} label={t('internal-ai-prompts:field_reasoning_level')}>
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('internal-ai-prompts:cancel')}</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {t('internal-ai-prompts:create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
